import { useListFilterContext } from "react-admin";
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText, Checkbox, Box, Typography } from "@mui/material";
import { useCallback } from "react";

interface FilterOption {
  label: string;
  value: string;
}

interface MultiSelectFilterListProps {
  /** The filter key (e.g. "status") */
  source: string;
  /** Section label */
  label: string;
  /** Optional icon to match React Admin's FilterList header */
  icon?: React.ReactElement | null;
  /** Available options */
  options: FilterOption[];
}

/**
 * A multi-select filter component styled to match React Admin's FilterList/FilterListItem.
 * Sends the filter as an array (e.g. status: ["pending", "approved"])
 * which the data provider serializes as repeated query params (status=pending&status=approved).
 */
export const MultiSelectFilterList = ({ source, label, icon, options }: MultiSelectFilterListProps) => {
  const { filterValues, setFilters, displayedFilters } = useListFilterContext();

  const currentValues: string[] = Array.isArray(filterValues[source])
    ? filterValues[source]
    : filterValues[source]
      ? [filterValues[source]]
      : [];

  const handleToggle = useCallback(
    (value: string) => {
      const isSelected = currentValues.includes(value);
      let newValues: string[];

      if (isSelected) {
        newValues = currentValues.filter((v) => v !== value);
      } else {
        newValues = [...currentValues, value];
      }

      const newFilters = { ...filterValues };
      if (newValues.length === 0) {
        delete newFilters[source];
      } else {
        newFilters[source] = newValues;
      }

      setFilters(newFilters, displayedFilters);
    },
    [currentValues, filterValues, displayedFilters, setFilters, source]
  );

  return (
    <List dense disablePadding>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, pt: 1.5, pb: 0.5 }}>
        {icon !== null && icon !== undefined && <Box sx={{ display: "flex" }}>{icon}</Box>}
        <Typography variant="overline">{label}</Typography>
      </Box>
      {options.map((option) => {
        const isSelected = currentValues.includes(option.value);
        return (
          <ListItem key={option.value} disablePadding>
            <ListItemButton
              dense
              onClick={() => handleToggle(option.value)}
              selected={isSelected}
              sx={{
                py: 0.25,
                px: 2,
                "&.Mui-selected": {
                  bgcolor: "action.selected",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 24 }}>
                <Checkbox edge="start" checked={isSelected} tabIndex={-1} disableRipple size="small" sx={{ p: 0 }} />
              </ListItemIcon>
              <ListItemText
                primary={option.label}
                primaryTypographyProps={{
                  variant: "body2",
                  fontSize: "0.8125rem",
                }}
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
};
